import { elenchiPriorita, vociCommesseAperte, TITOLI_ELENCHI } from "../utils/prioritaOperative";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useValoriCommesse, conValore } from "../utils/valoriCommesse";
import InsightButton from "../components/InsightButton";
import SuggerimentiOperativiWidget from "../components/SuggerimentiOperativiWidget";
import FatturazioneDashboardWidget from "../components/FatturazioneDashboardWidget";
import { CommessaLink, ClienteLink } from "../components/EntityLink";
import { collection, query, where, onSnapshot, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { useOrg } from "../contexts/OrgContext";
import { fonteOreAzienda } from "../utils/oreCommessa";
import { useFeatures } from "../contexts/FeaturesContext";
import { calcClienteHealthScore } from "../utils/clienteHelpers";
import { calcolaTotaliEO } from "../components/ElaborazioneOffertaTab";
import { differenceInDays, parseISO, format } from "date-fns";
import { isTaskOverdue } from "../utils/taskHelpers";
import { it } from "date-fns/locale";
import { EmptyState } from "../utils/EmptyState";
import { toLocalDateStr } from "../utils/dateUtils";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Activity, AlertTriangle, CheckCircle, Clock, Users, Folder,
  TrendingUp, TrendingDown, ChevronUp, ChevronDown,
  Search, X, Heart, Zap, Target, MessageSquare, Info, Building2
} from "lucide-react";
import { economiaSintetica, mappeEconomicheOrg, testoAggiornamentoCosti } from "../utils/economiaCommessa";

// ─── Health Score ─────────────────────────────────────────────────────
function calcHealthScore(tasks) {
  if (!tasks.length) return { score: null, label: "Non avviato", color: "#9ca3af" };
  const total = tasks.length;
  const completed = tasks.filter(t => t.status === "completata").length;
  const overdue = tasks.filter(t => isTaskOverdue(t)).length;
  const critical = tasks.filter(t => t.priority === "alta" && t.status !== "completata").length;
  const recent = tasks.filter(t => {
    const d = t.updatedAt?.toDate?.();
    return d && differenceInDays(new Date(), d) <= 7;
  }).length;
  let score = 100;
  score -= Math.min(40, overdue * 10);
  score -= Math.min(20, critical * 5);
  score -= total > 0 ? Math.max(0, 20 - Math.round(completed / total * 20)) : 0;
  score += recent > 0 ? Math.min(10, recent * 2) : -10;
  score = Math.max(0, Math.min(100, Math.round(score)));
  if (score >= 90) return { score, label: "Eccellente", color: "#059669" };
  if (score >= 70) return { score, label: "Buono", color: "#2563eb" };
  if (score >= 50) return { score, label: "Attenzione", color: "#d97706" };
  return { score, label: "Critico", color: "#dc2626" };
}

// ─── StatusBadge ──────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    ritardo:    { bg:"#fee2e2", color:"#dc2626", label:"In ritardo" },
    attivo:     { bg:"#d1fae5", color:"#059669", label:"Attivo" },
    completato: { bg:"#dbeafe", color:"#2563eb", label:"Completato" },
    non_avviato:{ bg:"#f3f4f6", color:"#6b7280", label:"Non avviato" },
  };
  const s = map[status] || map.attivo;
  return <span style={{ background:s.bg, color:s.color, padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600 }}>{s.label}</span>;
}

// ─── HealthBadge ──────────────────────────────────────────────────────
function coloreSuViola(hex) {
  const mappa = { "#059669":"#4ADE80", "#2563eb":"#7DA6FF", "#d97706":"#FBBF67", "#dc2626":"#F87171", "#9ca3af":"#C4C4CE" };
  return mappa[hex] || "#fff";
}

function HealthBadge({ score, color }) {
  const r = 18; const circ = 2*Math.PI*r; const dash = score===null ? 0 : (score/100)*circ;
  return (
    <div style={{ position:"relative", width:44, height:44 }}>
      <svg width={44} height={44}>
        <circle cx={22} cy={22} r={r} fill="none" stroke="#f3f4f6" strokeWidth={3}/>
        <circle cx={22} cy={22} r={r} fill="none" stroke={color} strokeWidth={3}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 22 22)"/>
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color }}>
        {score===null ? "–" : score}
      </div>
    </div>
  );
}

// ─── KPI Card cliccabile ──────────────────────────────────────────────
function KPICard({ icon: Icon, label, value, sub, color, onClick, critico }) {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick}
      onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)}
      style={{ background: critico ? "#fee2e2" : "#f9f9fb", border: critico ? "1.5px solid #dc2626" : "1px solid #e5e5ea", borderRadius:10, padding:"10px 12px", cursor: onClick ? "pointer" : "default", transition:"all 0.15s", opacity: hover && onClick ? 0.85 : 1 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
        <Icon size={13} color={critico ? "#a32d2d" : "#8b8b95"}/>
        {onClick && <span style={{ fontSize:9, color: critico ? "#a32d2d" : "#7c3aed", opacity: hover ? 1 : 0, transition:"opacity 0.15s", fontWeight:600 }}>Vedi →</span>}
      </div>
      <div style={{ fontSize:20, fontWeight:500, color: critico ? "#791f1f" : "#26215c", lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:10, color: critico ? "#a32d2d" : "#8b8b95", marginTop:4 }}>{label}</div>
      {sub && <div style={{ fontSize:9, color: critico ? "#a32d2d" : "#a8a8b3", marginTop:1 }}>{sub}</div>}
    </div>
  );
}

// ─── Tile Situazione Operativa ────────────────────────────────────────
function OpTile({ label, value, onClick, critico }) {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick}
      onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)}
      style={{ background: critico ? "#fee2e2" : "#f9f9fb", border: critico ? "1.5px solid #dc2626" : "1px solid #e5e5ea", borderRadius:10, padding:"10px 12px", cursor:"pointer", transition:"all 0.15s", flex:1, minWidth:110, opacity: hover ? 0.85 : 1 }}>
      <div style={{ fontSize:20, fontWeight:500, color: critico ? "#791f1f" : "#26215c", lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:10, color: critico ? "#a32d2d" : "#8b8b95", marginTop:4 }}>{label}</div>
    </div>
  );
}

// ─── Lista drill-down ─────────────────────────────────────────────────
function DrillDownList({ title, tasks, projects, allTasks, onClose }) {
  // Per le sottoattività il projectId non è salvato direttamente: lo risolviamo
  // tramite il parentTaskId che punta a un task che invece ha già projectId.
  const taskById = {};
  (allTasks||[]).forEach(t => { taskById[t.id] = t; });

  return (
    <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:0, overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", borderBottom:"1px solid #e5e7eb", background:"#f9fafb" }}>
        <span style={{ fontSize:14, fontWeight:700 }}>{title} <span style={{ color:"#9ca3af", fontWeight:400 }}>({tasks.length})</span></span>
        <button onClick={onClose} style={{ display:"flex", alignItems:"center", gap:5, background:"#fee2e2", border:"1px solid #fca5a5", borderRadius:8, cursor:"pointer", color:"#dc2626", fontSize:12, fontWeight:700, padding:"5px 12px", fontFamily:"'Inter',sans-serif" }}>
          ✕ Chiudi
        </button>
      </div>
      <div style={{ maxHeight:320, overflowY:"auto" }}>
        {tasks.length === 0
          ? <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:13 }}>Nessuna attività</div>
          : tasks.map(t => {
            // Risolvi projectId: diretto se è un task, tramite il padre se è una sottoattività
            const effectiveProjectId = t.projectId || (t.parentTaskId && taskById[t.parentTaskId]?.projectId);
            const proj = projects.find(p => p.id === effectiveProjectId);
            const overdue = isTaskOverdue(t);
            return (
              <div key={t.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 18px", borderBottom:"1px solid #f3f4f6" }}>
                <div className={`priority-dot ${t.priority||"media"}`} style={{ flexShrink:0 }}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, color: overdue ? "#dc2626" : "#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {t.parentTaskId && <span style={{color:"#9ca3af",fontWeight:400}}>↳ </span>}
                    {t.title}
                  </div>
                  <div style={{ fontSize:11, color:"#9ca3af", marginTop:1 }}>
                    {proj
                      ? <span style={{ marginRight:8, color:"#374151", fontWeight:600 }}>📁 {proj.name}</span>
                      : <span style={{ marginRight:8, color:"#d1d5db", fontStyle:"italic" }}>📁 Progetto non disponibile</span>}
                    {t.parentTaskTitle && <span style={{ marginRight:8, color:"#9ca3af" }}>di "{t.parentTaskTitle}"</span>}
                    {t.assignedName && <span style={{ marginRight:8 }}>👤 {t.assignedName}</span>}
                    {t.dueDate && <span style={{ color: overdue?"#dc2626":"#9ca3af" }}>📅 {t.dueDate.split("-").reverse().join("/")}{t.dueTime?" "+t.dueTime:""}</span>}
                  </div>
                </div>
                <span className={`badge badge-${t.priority}`}>{t.priority}</span>
              </div>
            );
          })
        }
      </div>
    </div>
  );
}

// ─── Componente principale ────────────────────────────────────────────
export default function ControlCenter({ onNavigate, deepLink = null }) {
  const { effectiveOrgId, orgVisitata } = useOrg();
  const fontOreOrg = orgVisitata ? fonteOreAzienda(orgVisitata) : undefined;
  const { currentUser, canSeeEconomic, isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const puoAccedere = isOrgAdmin || isPlatformOwner || canSee("control-center");
  const { hasFeature } = useFeatures();
  const isPro = hasFeature("kpi"); // indicatori e analisi cliente: piano Pro (listino Rev1)
  const [projectsRaw, setProjects] = useState([]);
  const [dipMap, setDipMap] = useState({}); // nexoraUid -> id dipendente, per aprire la scheda esatta
  const [tasksRaw, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [allCommesse, setAllCommesse] = useState([]); // tutti i progetti (incluso archiviati) per stats clienti
  const [programmazioni, setProgrammazioni] = useState([]); // per calcolo ore totali per commessa
  const [scadenze, setScadenze] = useState([]); // per card scadenze in alto
  const [projectTasks, setProjectTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [eventi, setEventi] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState("tutti");
  const [auditLog, setAuditLog] = useState([]);
  const [auditFiltroSezione, setAuditFiltroSezione] = useState("tutti");
  const [auditFiltroTesto, setAuditFiltroTesto] = useState("");
  const [filtroTesto, setFiltroTesto] = useState("");
  const [drillDown, setDrillDown] = useState(null); // { title, tasks }
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("tutti");
  const [filterUser, setFilterUser] = useState("tutti");
  const [filterCliente, setFilterCliente] = useState("tutti");
  const [filtroScadenza, setFiltroScadenza] = useState("tutte");
  const [sortBy, setSortBy] = useState("health");
  const [sortDir, setSortDir] = useState("asc");
  const [budgetPerProject, setBudgetPerProject] = useState({});
  const [costiPerProject, setCostiPerProject] = useState({});
  const [costiAggiornati, setCostiAggiornati] = useState({}); // W453: quando il Controllo Economico ha calcolato manodopera e noleggi
  const today = toLocalDateStr(new Date());

  // Carica dati
  useEffect(() => {
    if (!effectiveOrgId) return;
    const unsubs = [];
    unsubs.push(onSnapshot(query(collection(db,"projects"), where("organizationId","==",effectiveOrgId)),
      snap => {
        const all = snap.docs.map(d=>({id:d.id,...d.data()}));
        setProjects(all.filter(p=>!p.archived && !p.isParent));
        setAllCommesse(all);
        setLoading(false);
      }
    ));
    if (isPro) {
      unsubs.push(onSnapshot(query(collection(db,"clienti"), where("organizationId","==",effectiveOrgId)),
        snap => setClienti(snap.docs.map(d=>({id:d.id,...d.data()})))
      ));
    }
    unsubs.push(onSnapshot(query(collection(db,"tasks"), where("organizationId","==",effectiveOrgId)),
      snap => setTasks(snap.docs.map(d=>({id:d.id,...d.data()})))
    ));
    // Budget e costi reali, per il rischio margine — solo per chi può vedere dati economici
    if (canSeeEconomic) {
      // Ricavi e costi con le stesse regole del Controllo Economico (utils/economiaCommessa.js):
      // ricavi = importo commessa + ricavi registrati; costi = movimenti di costo + ordini
      // di acquisto IVA esclusa. Si ricalcolano quando cambia una delle tre fonti.
      const fonti = { budget: [], movimenti: [], ordini: [] };
      const ricalcola = () => {
        const { ricavi, costi, aggiornati } = mappeEconomicheOrg({ ...fonti, fontOre: fontOreOrg });
        setBudgetPerProject(ricavi); setCostiPerProject(costi); setCostiAggiornati(aggiornati);
      };
      unsubs.push(onSnapshot(query(collection(db,"budgetCommessa"), where("organizationId","==",effectiveOrgId)),
        snap => { fonti.budget = snap.docs.map(d=>({id:d.id,...d.data()})); ricalcola(); }));
      unsubs.push(onSnapshot(query(collection(db,"movimentiEconomici"), where("organizationId","==",effectiveOrgId)),
        snap => { fonti.movimenti = snap.docs.map(d=>d.data()); ricalcola(); }));
      unsubs.push(onSnapshot(query(collection(db,"ordiniAcquisto"), where("organizationId","==",effectiveOrgId)),
        snap => { fonti.ordini = snap.docs.map(d=>d.data()); ricalcola(); }));
    }
    unsubs.push(onSnapshot(query(collection(db,"dipendenti"), where("organizationId","==",effectiveOrgId)),
      snap => {
        const m = {};
        snap.docs.forEach(d => { const dip=d.data(); if (dip.nexoraUid) m[dip.nexoraUid]=d.id; });
        setDipMap(m);
      }
    ));
    // Sottoattività: filtrate per organizationId (tutti i nuovi subtask lo hanno già)
    unsubs.push(onSnapshot(
      query(collection(db,"subtasks"), where("organizationId","==",effectiveOrgId)),
      snap => setSubtasks(snap.docs.map(d=>({id:d.id,...d.data()})))
    ));
    // Carica scadenzario per card in alto
    unsubs.push(onSnapshot(
      query(collection(db,"scadenzario"), where("organizationId","==",effectiveOrgId)),
      snap => setScadenze(snap.docs.map(d=>({id:d.id,...d.data()})))
    ));
    // Carica programmazioni settimanali per calcolare ore totali per commessa
    unsubs.push(onSnapshot(
      query(collection(db,"programmazioniSettimanali"), where("organizationId","==",effectiveOrgId)),
      snap => setProgrammazioni(snap.docs.map(d=>({id:d.id,...d.data()})))
    ));
    unsubs.push(onSnapshot(query(collection(db,"users"), where("organizationId","==",effectiveOrgId)),
      snap => setUsers(snap.docs.map(d=>d.data()).filter(u=>u.enabled))
    ));
    if (currentUser) {
      unsubs.push(onSnapshot(query(collection(db,"conversations"), where("organizationId","==",effectiveOrgId), where("members","array-contains",currentUser.uid)),
        snap => setConversations(snap.docs.map(d=>({id:d.id,...d.data()})))
      ));
    }
    return () => unsubs.forEach(u=>u());
  }, [effectiveOrgId, currentUser, isPro, fontOreOrg]);

  // Esclude le commesse "senza cliente" (aree di lavoro personali) dalle statistiche aziendali —
  // unico punto di filtro, il resto del file continua a usare "projects"/"tasks" come prima.
  const projects = useMemo(()=>projectsRaw, [projectsRaw]);
  const tasks = useMemo(()=>{
    const idsVisibili = new Set(projects.map(p=>p.id));
    return tasksRaw.filter(t=>idsVisibili.has(t.projectId));
  }, [tasksRaw, projects]);

  useEffect(() => {
    const map = {};
    tasks.forEach(t => { if(!t.projectId) return; if(!map[t.projectId]) map[t.projectId]=[]; map[t.projectId].push(t); });
    setProjectTasks(map);
  }, [tasks]);

  // ─── Stats ────────────────────────────────────────────────────────
  // Filtra le sottoattività mantenendo solo quelle il cui task padre appartiene a questa organizzazione
  // (necessario perché molti record storici non hanno organizationId salvato)
  const taskIds = useMemo(() => new Set(tasks.map(t => t.id)), [tasks]);
  const orgSubtasks = useMemo(() => subtasks.filter(s => taskIds.has(s.parentTaskId)), [subtasks, taskIds]);

  const allItems = useMemo(() => [...tasks, ...orgSubtasks], [tasks, orgSubtasks]);
  const overdueTasks   = useMemo(() => allItems.filter(t => isTaskOverdue(t)), [allItems]);
  const openTasks      = useMemo(() => allItems.filter(t => t.status==="aperta"||t.status==="in_corso"), [allItems]);
  const criticalTasks  = useMemo(() => allItems.filter(t => t.priority==="alta" && t.status!=="completata" && t.status!=="annullata"), [allItems]);
  const completedToday = useMemo(() => allItems.filter(t => {
    if (t.status !== "completata") return false;
    const d = t.updatedAt?.toDate?.();
    return d && toLocalDateStr(d) === today;
  }), [allItems, today]);
  const dueToday = useMemo(() => allItems.filter(t => t.dueDate === today && t.status !== "completata" && t.status !== "annullata"), [allItems, today]);
  const overdueProjects = useMemo(() => projects.filter(p => (projectTasks[p.id]||[]).some(t=>isTaskOverdue(t))), [projects, projectTasks]);

  // Clienti che vanno meglio del previsto — stesso dato di "Scostamenti Margine" (Sezione
  // Commerciale), ma capovolto: qui cerchiamo chi ti sta andando bene, non chi preoccupa.
  const [clientiTop, setClientiTop] = useState([]);
  useEffect(() => {
    if (!effectiveOrgId || !canSeeEconomic) { setClientiTop([]); return; }
    (async () => {
      const [offSnap, segSnap] = await Promise.all([
        getDocs(query(collection(db,"elaborazioniOfferte"), where("orgId","==",effectiveOrgId))),
        getDocs(query(collection(db,"segnalazioniCliente"), where("orgId","==",effectiveOrgId))),
      ]);
      const collegate = offSnap.docs.map(d=>({id:d.id,...d.data()})).filter(e=>e.projectIdCollegato && e.clienteId);
      const ordiniOrg = (await getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",effectiveOrgId)))).docs.map(d=>d.data());
      const perCliente = {};
      await Promise.all(collegate.map(async (e) => {
        const [budgetSnap, movSnap] = await Promise.all([
          getDocs(query(collection(db,"budgetCommessa"), where("projectId","==",e.projectIdCollegato), where("organizationId","==",effectiveOrgId))),
          getDocs(query(collection(db,"movimentiEconomici"), where("projectId","==",e.projectIdCollegato), where("organizationId","==",effectiveOrgId))),
        ]);
        const { ricavi, costi } = economiaSintetica({ budget: budgetSnap.empty?null:budgetSnap.docs[0].data(), movimenti: movSnap.docs.map(d=>d.data()), ordini: ordiniOrg, projectId: e.projectIdCollegato });
        if (ricavi<=0) return;
        const margineReale = (ricavi-costi)/ricavi*100;
        const totali = calcolaTotaliEO(e);
        const marginePrevisto = totali.totaleGenerale>0 ? (totali.totaleMargini/totali.totaleGenerale*100) : 0;
        const key = e.clienteId;
        if (!perCliente[key]) perCliente[key] = { clienteNome:e.clienteNome, scostamenti:[] };
        perCliente[key].scostamenti.push(marginePrevisto - margineReale); // negativo = va meglio del previsto
      }));
      const segnalazioniPerCliente = {};
      segSnap.docs.forEach(d => {
        const s = d.data();
        if (!s.clienteId || !(s.priorita==="alta"||s.priorita==="urgente")) return;
        segnalazioniPerCliente[s.clienteId] = (segnalazioniPerCliente[s.clienteId]||0)+1;
      });

      const classifica = Object.entries(perCliente)
        .filter(([,c])=>c.scostamenti.length>=2)
        .map(([clienteId,c]) => ({
          clienteId, clienteNome:c.clienteNome,
          scostamentoMedio: c.scostamenti.reduce((s,v)=>s+v,0)/c.scostamenti.length,
          nOfferte: c.scostamenti.length,
          segnalazioniCritiche: segnalazioniPerCliente[clienteId]||0,
        }))
        .filter(c => c.scostamentoMedio<=0 && c.segnalazioniCritiche===0) // margine reale in linea o sopra il previsto, zero criticità
        .sort((a,b)=>a.scostamentoMedio-b.scostamentoMedio)
        .slice(0,5);
      setClientiTop(classifica);
    })();
  }, [effectiveOrgId, canSeeEconomic]);

  const unreadChats = useMemo(() => conversations.filter(c => {
    if (!c.lastMessageAt || !c.lastMessage) return false;
    const lastRead = c.lastReadBy?.[currentUser?.uid];
    if (!lastRead) return true;
    return c.lastMessageAt.toMillis?.() > lastRead.toMillis?.();
  }).length, [conversations, currentUser]);

  const completionRate = allItems.length > 0 ? Math.round(allItems.filter(t=>t.status==="completata").length / allItems.length * 100) : 0;

  // Statistiche scadenze per card in alto
  const scadenzeStats = useMemo(()=>{
    const oggi = new Date(); oggi.setHours(0,0,0,0);
    const vociConData = scadenze.filter(s=>s.dataScadenza);
    const scadute = vociConData.filter(s=>{ const g=Math.round((new Date(s.dataScadenza+"T00:00:00")-oggi)/(1000*60*60*24)); return g<0; }).length;
    const sette = vociConData.filter(s=>{ const g=Math.round((new Date(s.dataScadenza+"T00:00:00")-oggi)/(1000*60*60*24)); return g>=0&&g<=7; }).length;
    const trenta = vociConData.filter(s=>{ const g=Math.round((new Date(s.dataScadenza+"T00:00:00")-oggi)/(1000*60*60*24)); return g>7&&g<=30; }).length;
    const programmate = vociConData.filter(s=>{ const g=Math.round((new Date(s.dataScadenza+"T00:00:00")-oggi)/(1000*60*60*24)); return g>30; }).length;
    return { scadute, sette, trenta, programmate };
  },[scadenze]);

  // Ore totali per commessa aggregate da tutta la programmazione storica
  const orePerCommessa = useMemo(() => {
    const mappa = {}; // projectId -> ore totali
    programmazioni.forEach(prog => {
      Object.values(prog.celle || {}).forEach(voci => {
        if (!Array.isArray(voci)) return;
        voci.forEach(voce => {
          if (!voce.commessaId) return;
          mappa[voce.commessaId] = (mappa[voce.commessaId]||0) + (parseFloat(voce.ore)||0);
        });
      });
    });
    return mappa;
  }, [programmazioni]);

  // W420: valore di ogni commessa dal budget (importo + varianti accettate), non dal vecchio campo
  const valoriCommesse = useValoriCommesse(effectiveOrgId, isPro);
  const allCommesseV = useMemo(() => conValore(allCommesse, valoriCommesse), [allCommesse, valoriCommesse]);
  // ─── Statistiche aggregate Clienti (widget Centro Operativo, solo Pro) ──
  const clientiStats = useMemo(() => {
    if (!isPro || clienti.length === 0) return null;
    const now = new Date();
    const inizioMese = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const nuoviMese = clienti.filter(c => (c.createdAt?.toMillis?.()||0) >= inizioMese).length;

    let attivi = 0, fatturatoTotale = 0, miglior = null, migliorValore = -1;
    const valorePerSettore = {};
    clienti.forEach(c => {
      const commesseC = allCommesseV.filter(p => p.clienteId === c.id);
      const valC = commesseC.reduce((s,p)=>s+(parseFloat(p.valoreEconomico)||0),0);
      fatturatoTotale += valC;
      if (commesseC.some(p => !p.archived)) attivi++;
      if (valC > migliorValore) { migliorValore = valC; miglior = c; }
      const settore = c.settore || "Non specificato";
      valorePerSettore[settore] = (valorePerSettore[settore]||0) + (valC || 1); // fallback 1 per contare anche clienti senza valore
    });

    // Distribuzione percentuale per settore (basata sul valore economico totale)
    const totaleSettori = Object.values(valorePerSettore).reduce((a,b)=>a+b,0);
    const distribuzioneSettori = Object.entries(valorePerSettore)
      .map(([nome, val]) => ({ nome, pct: totaleSettori>0 ? Math.round((val/totaleSettori)*100) : 0 }))
      .sort((a,b) => b.pct - a.pct)
      .slice(0, 5);

    return {
      totale: clienti.length,
      nuoviMese,
      attivi,
      fatturatoTotale,
      migliorNome: miglior?.ragioneSociale || miglior?.nome || "—",
      distribuzioneSettori,
      preferiti: clienti.filter(c => c.preferito),
    };
  }, [isPro, clienti, allCommesseV]);

  // ─── Progetti arricchiti ──────────────────────────────────────────
  const enrichedProjects = useMemo(() => projects.map(p => {
    const pt = projectTasks[p.id] || [];
    const total = pt.length;
    const completed = pt.filter(t=>t.status==="completata").length;
    const overdue = pt.filter(t=>isTaskOverdue(t)).length;
    const open = pt.filter(t=>t.status==="aperta"||t.status==="in_corso").length;
    const progress = total > 0 ? Math.round(completed/total*100) : 0;
    const health = calcHealthScore(pt);
    const lastUpdated = pt.reduce((acc,t)=>{ const d=t.updatedAt?.toMillis?.()||0; return d>acc?d:acc; }, p.createdAt?.toMillis?.()||0);
    const responsibili = [...new Set(pt.filter(t=>t.assignedName).map(t=>t.assignedName))];
    const status = total===0?"non_avviato" : pt.every(t=>t.status==="completata"||t.status==="annullata")?"completato" : overdue>0?"ritardo":"attivo";
    // Rischio margine: budget vs costi reali già sostenuti — solo dove entrambi i dati esistono
    const budget = budgetPerProject[p.id]||0;
    const costiReali = costiPerProject[p.id]||0;
    const marginePerc = budget>0 ? ((budget-costiReali)/budget*100) : null;
    return { ...p, total, completed, overdue, open, progress, health, lastUpdated, responsibili, status, marginePerc };
  }), [projects, projectTasks, budgetPerProject, costiPerProject]);

  // Commesse ancora aperte il cui margine attuale (budget - costi reali già sostenuti) sta
  // scendendo verso zero o sotto — un preavviso, non un consuntivo a lavoro finito.
  const commesseARischioMargine = useMemo(() =>
    enrichedProjects.filter(p => p.marginePerc!==null && p.marginePerc<15).sort((a,b)=>a.marginePerc-b.marginePerc),
    [enrichedProjects]);

  // ─── Carico utenti ────────────────────────────────────────────────
  const userLoad = useMemo(() => {
    const map = {};
    tasks.filter(t=>t.assignedName&&t.status!=="completata").forEach(t=>{
      if(!map[t.assignedName]) map[t.assignedName]={name:t.assignedName,uid:t.assignedTo||null,open:0,overdue:0};
      map[t.assignedName].open++;
      if(isTaskOverdue(t)) map[t.assignedName].overdue++;
    });
    return Object.values(map).sort((a,b)=>b.open-a.open).slice(0,8);
  }, [tasks]);

  // ─── Alert ────────────────────────────────────────────────────────
  const alerts = useMemo(() => {
    const list = [];
    // Ogni alert porta a un punto che rispecchia davvero il suo testo — non un'unica
    // destinazione condivisa "a caso" tra alert diversi, come successo nella modifica precedente.
    const vaiProgettiPerRitardo = () => { setActiveTab("projects"); setSortBy("overdue"); setSortDir("desc"); };
    const vaiProgetti = () => { setActiveTab("projects"); };
    if (overdueTasks.length>0) list.push({type:"error",icon:AlertTriangle,text:`${overdueTasks.length} attività scadute richiedono attenzione immediata`, onClick:vaiProgettiPerRitardo});
    const unassignedCritical = tasks.filter(t=>t.priority==="alta"&&!t.assignedTo&&t.status!=="completata");
    if (unassignedCritical.length>0) list.push({type:"warning",icon:Zap,text:`${unassignedCritical.length} attività critiche senza responsabile`, onClick:vaiProgetti});
    userLoad.filter(u=>u.open>10).forEach(u=>{
      const dipId = u.uid && dipMap[u.uid];
      list.push({type:"warning",icon:Users,text:`${u.name} ha ${u.open} attività in corso — possibile sovraccarico`,
        onClick:()=>onNavigate && onNavigate(dipId ? {page:"dipendenti", dipId} : {page:"dipendenti"})});
    });
    projects.forEach(p=>{ if(!projectTasks[p.id]||projectTasks[p.id].length===0) list.push({type:"info",icon:Folder,text:`Il progetto "${p.name}" non ha ancora attività`, onClick:()=>onNavigate&&onNavigate({page:"project",project:p})}); });
    const imminent = tasks.filter(t=>{ if(!t.dueDate||t.status==="completata") return false; const d=differenceInDays(parseISO(t.dueDate),new Date()); return d>=0&&d<=3; });
    if (imminent.length>0) list.push({type:"warning",icon:Clock,text:`${imminent.length} attività in scadenza nei prossimi 3 giorni`, onClick:vaiProgetti});
    return list;
  }, [tasks, overdueTasks, projects, projectTasks, userLoad, onNavigate, dipMap]);

  // ─── Filtro tabella progetti ──────────────────────────────────────
  const filteredProjects = useMemo(() => {
    let list = [...enrichedProjects];
    list = list.filter(p=>p.statoCommessa!=="chiusa");
    if (search) list = list.filter(p=>p.name?.toLowerCase().includes(search.toLowerCase()));
    if (filterStatus!=="tutti") list = list.filter(p=>p.status===filterStatus);
    if (filterUser!=="tutti") list = list.filter(p=>p.responsibili.includes(filterUser));
    if (filterCliente!=="tutti") list = list.filter(p=>p.clienteNome===filterCliente);
    if (filtroScadenza!=="tutte") {
      const oggi2 = new Date(); oggi2.setHours(0,0,0,0);
      const tra7 = new Date(oggi2.getTime()+7*86400000);
      const fineMese = new Date(oggi2.getFullYear(), oggi2.getMonth()+1, 0);
      list = list.filter(p=>{
        if (!p.dataFine) return filtroScadenza==="senza_data";
        const df = new Date(p.dataFine+"T00:00:00");
        if (filtroScadenza==="scadute") return df < oggi2;
        if (filtroScadenza==="7gg") return df >= oggi2 && df <= tra7;
        if (filtroScadenza==="mese") return df >= oggi2 && df <= fineMese;
        return true;
      });
    }
    list.sort((a,b)=>{
      let va,vb;
      switch(sortBy){
        case "health": va=a.health.score??-1; vb=b.health.score??-1; break;
        case "progress": va=a.progress; vb=b.progress; break;
        case "overdue": va=a.overdue; vb=b.overdue; break;
        case "name": return sortDir==="asc"?a.name?.localeCompare(b.name):b.name?.localeCompare(a.name);
        case "updated": va=a.lastUpdated; vb=b.lastUpdated; break;
        default: va=a.health.score??-1; vb=b.health.score??-1;
      }
      return sortDir==="asc"?va-vb:vb-va;
    });
    return list;
  }, [enrichedProjects, search, filterStatus, filterUser, filterCliente, filtroScadenza, sortBy, sortDir]);

  const toggleSort = field => { if(sortBy===field) setSortDir(d=>d==="asc"?"desc":"asc"); else{setSortBy(field);setSortDir("desc");} };
  const SortIcon = ({field}) => sortBy===field ? (sortDir==="asc"?<ChevronUp size={12}/>:<ChevronDown size={12}/>) : <ChevronDown size={12} style={{opacity:0.3}}/>;
  const allResponsibili = [...new Set(enrichedProjects.flatMap(p=>p.responsibili))];
  const allClienti = [...new Set(enrichedProjects.map(p=>p.clienteNome).filter(Boolean))].sort();

  // ─── Grafico dati ─────────────────────────────────────────────────
  const pieData = [
    {name:"Attivi",value:enrichedProjects.filter(p=>p.status==="attivo").length,color:"#059669"},
    {name:"Completati",value:enrichedProjects.filter(p=>p.status==="completato").length,color:"#2563eb"},
    {name:"In ritardo",value:enrichedProjects.filter(p=>p.status==="ritardo").length,color:"#dc2626"},
    {name:"Non avviati",value:enrichedProjects.filter(p=>p.status==="non_avviato").length,color:"#9ca3af"},
  ].filter(d=>d.value>0);

  // Aperto da Nexiora Insight su un elenco preciso (utils/prioritaOperative.js): stesso
  // conteggio visto in Insight, elenco già aperto. Si apre una sola volta, a dati caricati.
  const elencoAperto = useRef(null);
  useEffect(() => {
    const chiave = deepLink?.elenco;
    if (!chiave || elencoAperto.current === deepLink) return;
    if (chiave === "commesse") { elencoAperto.current = deepLink; setActiveTab("projects"); return; } // «Vedi le commesse» da Insight
    if (!TITOLI_ELENCHI[chiave] || tasksRaw.length === 0) return;
    elencoAperto.current = deepLink;
    const lista = elenchiPriorita(vociCommesseAperte({ tasks: tasksRaw, subtasks, projects: projectsRaw }))[chiave] || [];
    setActiveTab("overview");
    setDrillDown({ title: TITOLI_ELENCHI[chiave], tasks: lista });
    setTimeout(() => document.getElementById("elenco-drill")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [deepLink, tasksRaw, subtasks, projectsRaw]);

  if (loading) return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Caricamento...</div>;

  const TABS = [
    {id:"overview", label:"📊 Panoramica"},
    {id:"projects", label:"📁 Progetti"},
    {id:"charts",   label:"📈 Grafici"},
    {id:"alerts",   label:`🚨 Alert${alerts.length>0?` (${alerts.length})`:""}`},
    {id:"feed",     label:`📡 Feed${eventi.length>0?` (${eventi.length})`:""}`},
    {id:"audit",    label:"📋 Log Modifiche"},
  ];

  const openDrill = (title, taskList) => {
    setDrillDown(d => (d && d.title === title) ? null : {title, tasks:taskList});
  };


  if (!puoAccedere) {
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center"}}>
        <div style={{fontSize:40,marginBottom:12}}>🔒</div>
        <div style={{fontSize:15,fontWeight:700,color:"#374151",marginBottom:6}}>Accesso riservato</div>
        <div style={{fontSize:13,color:"#9ca3af",maxWidth:360}}>Questa sezione è visibile solo a Titolare, Gestione aziendale e Project Manager.</div>
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",background:"#f8fafc"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px",flexShrink:0}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:800,letterSpacing:"-0.5px",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <Activity size={22} color="#7c3aed"/> Centro Operativo
            <InsightButton onNavigate={onNavigate}/>
            {TABS.map(tab=>(
              <button key={tab.id} onClick={()=>{setActiveTab(tab.id);setDrillDown(null);}}
                style={{padding:"5px 12px",border:`1px solid ${activeTab===tab.id?"#7c3aed":"#e5e7eb"}`,borderRadius:8,background:activeTab===tab.id?"#ede9fe":"#fff",color:activeTab===tab.id?"#7c3aed":"#374151",fontSize:12,fontWeight:activeTab===tab.id?600:400,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                {tab.label}
              </button>
            ))}
          </h2>
          <p style={{fontSize:13,color:"#6b7280",marginTop:4}}>
            Vista aggregata di {projects.length} progetti · aggiornata in tempo reale
          </p>
          {(new Date().getMonth()===11 || allCommesse.filter(p=>p.statoCommessa==="chiusa").length>=10) && (
            <button onClick={()=>onNavigate&&onNavigate({page:"il-tuo-anno"})}
              style={{marginTop:8,display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:"#fff",fontSize:12.5,fontWeight:700,cursor:"pointer"}}>
              🏆 Il tuo anno
            </button>
          )}
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"14px 20px"}}>

        {/* ─── PANORAMICA ─── */}
        {activeTab==="overview" && (<>

          {/* Riquadri spostati dalla Home (ottobre 2026): suggerimenti operativi e fatturazione di fine mese */}
          {canSee("suggerimenti-operativi") && (
            <SuggerimentiOperativiWidget currentUser={currentUser} isOrgAdmin={isOrgAdmin} isPlatformOwner={isPlatformOwner}
              effectiveOrgId={effectiveOrgId} onNavigate={onNavigate}/>
          )}
          <FatturazioneDashboardWidget currentUser={currentUser} isOrgAdmin={isOrgAdmin} isPlatformOwner={isPlatformOwner}
            effectiveOrgId={effectiveOrgId} onNavigate={onNavigate}/>

          {/* KPI cliccabili */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8,marginBottom:10}}>
            <KPICard icon={Folder}        label="Progetti totali"      value={projects.length}        color="#7c3aed" sub={`${enrichedProjects.filter(p=>p.status==="attivo").length} attivi`}
              onClick={()=>openDrill("Tutti i progetti — attività aperte", openTasks)}/>
            <KPICard icon={AlertTriangle} label="Progetti in ritardo"  value={overdueProjects.length} color="#dc2626" sub="con attività scadute" critico={overdueProjects.length>0}
              onClick={()=>openDrill("Progetti in ritardo", overdueProjects.flatMap(p=>projectTasks[p.id]||[]).filter(t=>isTaskOverdue(t)))}/>
            <KPICard icon={Clock}         label="Attività scadute"     value={overdueTasks.length}    color="#dc2626" critico={overdueTasks.length>0}
              onClick={()=>openDrill("Attività scadute", overdueTasks)}/>
            <KPICard icon={Activity}      label="Attività aperte"      value={openTasks.length}       color="#2563eb"
              onClick={()=>openDrill("Attività aperte", openTasks)}/>
            <KPICard icon={Target}        label="Attività critiche"    value={criticalTasks.length}   color="#d97706" sub="priorità alta"
              onClick={()=>openDrill("Attività critiche", criticalTasks)}/>
            <KPICard icon={CheckCircle}   label="Attività completate"  value={tasks.filter(t=>t.status==="completata").length} color="#059669" sub={`${completionRate}% del totale`}
              onClick={()=>openDrill("Attività completate", tasks.filter(t=>t.status==="completata"))}/>
            <KPICard icon={Users}         label="Utenti attivi"        value={users.length}           color="#0891b2"/>
            <KPICard icon={TrendingUp}    label="Tasso completamento"  value={`${completionRate}%`}   color="#7c3aed" critico={completionRate===0 && overdueTasks.length>0}/>
          </div>

          {/* Commesse a rischio margine + Clienti da coccolare — fianco a fianco, stesso
              pubblico (titolare/admin), sguardi opposti sugli stessi dati di fondo. */}
          {canSeeEconomic && (commesseARischioMargine.length>0 || clientiTop.length>0) && (
            <div style={{display:"grid",gridTemplateColumns: commesseARischioMargine.length>0 && clientiTop.length>0 ? "1fr 1fr" : "1fr", gap:16, marginBottom:16}}>
              {commesseARischioMargine.length>0 && (
                <div style={{background:"#fff",border:"1px solid #fecaca",borderRadius:12,padding:16}}>
                  <h3 style={{fontSize:13,fontWeight:700,color:"#991b1b",marginBottom:2,display:"flex",alignItems:"center",gap:6}}>
                    ⚠️ Commesse a rischio margine ({commesseARischioMargine.length})
                  </h3>
                  <p style={{fontSize:11,color:"#6b7280",marginBottom:10}}>Ancora aperte, margine attuale (budget meno costi già sostenuti) sceso sotto il 15% — un preavviso, non un consuntivo a lavoro finito.
                    {testoAggiornamentoCosti(commesseARischioMargine.map(p => costiAggiornati[p.id])) && ` Manodopera e noleggi aggiornati all'apertura del Controllo Economico (il meno recente il ${testoAggiornamentoCosti(commesseARischioMargine.map(p => costiAggiornati[p.id]))}).`}</p>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
                      {["Commessa","Cliente","Margine attuale"].map(h=>(
                        <th key={h} style={{padding:"5px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {commesseARischioMargine.map(p=>(
                        <tr key={p.id} style={{borderBottom:"1px solid #f9fafb",cursor:onNavigate?"pointer":"default"}}
                          onClick={()=>onNavigate&&onNavigate({page:"project",projectId:p.id,project:p})}>
                          <td style={{padding:"6px 8px",fontWeight:600,color:"#111827"}}>{p.codiceCommessa?`[${p.codiceCommessa}] `:""}{p.name}</td>
                          <td style={{padding:"6px 8px",color:"#6b7280"}}>{p.clienteNome||"—"}</td>
                          <td style={{padding:"6px 8px",fontWeight:700,color:p.marginePerc<0?"#dc2626":"#d97706"}}>{(p.marginePerc).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {clientiTop.length>0 && (
                <div style={{background:"#fff",border:"1px solid #bbf7d0",borderRadius:12,padding:16}}>
                  <h3 style={{fontSize:13,fontWeight:700,color:"#065f46",marginBottom:2,display:"flex",alignItems:"center",gap:6}}>
                    🏆 Clienti da coccolare ({clientiTop.length})
                  </h3>
                  <p style={{fontSize:11,color:"#6b7280",marginBottom:10}}>Margine reale in linea o sopra il previsto, zero segnalazioni critiche — su almeno 2 offerte concluse.</p>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
                      {["Cliente","Offerte","Margine vs previsto"].map(h=>(
                        <th key={h} style={{padding:"5px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {clientiTop.map((c,i)=>(
                        <tr key={c.clienteId} style={{borderBottom:"1px solid #f9fafb"}}>
                          <td style={{padding:"6px 8px",fontWeight:600,color:"#111827"}}>{["🥇","🥈","🥉"][i]||"🏅"} {c.clienteNome}</td>
                          <td style={{padding:"6px 8px",color:"#6b7280"}}>{c.nOfferte}</td>
                          <td style={{padding:"6px 8px",fontWeight:700,color:"#059669"}}>+{Math.abs(c.scostamentoMedio).toFixed(1)} punti</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Drill-down lista */}
          {drillDown && (
            <div id="elenco-drill" style={{marginBottom:20}}>
              <DrillDownList title={drillDown.title} tasks={drillDown.tasks} projects={projects} allTasks={tasks} onClose={()=>setDrillDown(null)}/>
            </div>
          )}

          {/* ── SITUAZIONE OPERATIVA ── */}
          <div style={{marginBottom:10}}>
            <h3 style={{fontSize:11,fontWeight:600,marginBottom:6,color:"#6b7280",textTransform:"uppercase",letterSpacing:".04em"}}>
              Situazione operativa
            </h3>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8}}>
              <OpTile label="Attività scadute"     value={overdueTasks.length}   critico={overdueTasks.length>0}
                onClick={()=>openDrill("Attività scadute", overdueTasks)}/>
              <OpTile label="In scadenza oggi"     value={dueToday.length}
                onClick={()=>openDrill("Attività in scadenza oggi", dueToday)}/>
              <OpTile label="Concluse oggi"        value={completedToday.length}
                onClick={()=>openDrill("Attività concluse oggi", completedToday)}/>
              <OpTile label="Chat non lette"       value={unreadChats}
                onClick={()=>alert("Vai alla sezione Chat per leggere i messaggi non letti.")}/>
            </div>
          </div>

          {/* ── WIDGET CLIENTI (solo Pro) ── */}
          {isPro && clientiStats && (
            <div onClick={()=>onNavigate && onNavigate({page:"clienti"})}
              style={{background:"#3C3489",borderRadius:10,padding:"12px 14px",marginBottom:10,cursor:"pointer",transition:"opacity 0.15s"}}
              onMouseOver={e=>e.currentTarget.style.opacity="0.92"}
              onMouseOut={e=>e.currentTarget.style.opacity="1"}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <h3 style={{fontSize:12,fontWeight:500,display:"flex",alignItems:"center",gap:6,color:"#fff",margin:0}}>
                  <Building2 size={13} color="#AFA9EC"/> Clienti
                </h3>
                <span style={{fontSize:10,color:"#AFA9EC",fontWeight:600}}>Apri Centro Clienti →</span>
              </div>
              <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
                <div>
                  <div style={{fontSize:10,color:"#AFA9EC"}}>Totale</div>
                  <div style={{fontSize:15,fontWeight:500,color:"#fff"}}>{clientiStats.totale}</div>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#AFA9EC"}}>Nuovi questo mese</div>
                  <div style={{fontSize:15,fontWeight:500,color:"#63A166"}}>+{clientiStats.nuoviMese}</div>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#AFA9EC"}}>Clienti attivi</div>
                  <div style={{fontSize:15,fontWeight:500,color:"#fff"}}>{clientiStats.attivi}</div>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#AFA9EC"}}>Fatturato commesse</div>
                  <div style={{fontSize:15,fontWeight:500,color:"#fff"}}>€ {clientiStats.fatturatoTotale.toLocaleString("it-IT",{maximumFractionDigits:0})}</div>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#AFA9EC"}}>Miglior cliente</div>
                  <div style={{fontSize:13,fontWeight:500,color:"#fff"}}>{clientiStats.migliorNome}</div>
                </div>
              </div>

              {/* Distribuzione per settore */}
              {clientiStats.distribuzioneSettori.length > 0 && (
                <div style={{marginTop:10, paddingTop:8, borderTop:"1px solid rgba(255,255,255,.12)"}}>
                  <div style={{fontSize:10, color:"#AFA9EC", marginBottom:5}}>Distribuzione per settore</div>
                  <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
                    {clientiStats.distribuzioneSettori.map(s => (
                      <div key={s.nome} style={{display:"flex", alignItems:"center", gap:5}}>
                        <span style={{fontSize:11, fontWeight:500, color:"#fff"}}>{s.pct}%</span>
                        <span style={{fontSize:11, color:"#AFA9EC"}}>{s.nome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clienti preferiti */}
              {clientiStats.preferiti.length > 0 && (
                <div style={{marginTop:8, paddingTop:8, borderTop:"1px solid rgba(255,255,255,.12)", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
                  <span style={{fontSize:10, color:"#AFA9EC"}}>⭐ Preferiti:</span>
                  {clientiStats.preferiti.slice(0,5).map(c => (
                    <span key={c.id} style={{fontSize:11, fontWeight:500, color:"#fff", background:"rgba(255,255,255,.1)", padding:"2px 8px", borderRadius:10}}>{c.ragioneSociale||c.nome}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Alert + Health */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <div style={{background:"#3C3489",borderRadius:10,padding:12}}>
              <h3 style={{fontSize:12,fontWeight:500,marginBottom:8,display:"flex",alignItems:"center",gap:6,color:"#fff"}}>
                <AlertTriangle size={13} color="#AFA9EC"/> Alert ({alerts.length})
              </h3>
              {alerts.length===0
                ? <p style={{fontSize:11,color:"#AFA9EC"}}>✅ Nessun alert attivo</p>
                : alerts.slice(0,5).map((a,i)=>(
                  <div key={i} onClick={a.onClick} style={{display:"flex",alignItems:"flex-start",gap:6,padding:"5px 0",borderBottom:"1px solid rgba(255,255,255,.08)",cursor:a.onClick?"pointer":"default"}}
                    onMouseOver={e=>{if(a.onClick)e.currentTarget.style.opacity="0.75";}}
                    onMouseOut={e=>{e.currentTarget.style.opacity="1";}}>
                    <a.icon size={12} color={a.type==="error"?"#E24B4A":a.type==="warning"?"#EF9F27":"#7F77DD"} style={{marginTop:1,flexShrink:0}}/>
                    <span style={{fontSize:11,color:"#CECBF6",lineHeight:1.4}}>{a.text}</span>
                  </div>
                ))
              }
            </div>
            <div style={{background:"#3C3489",borderRadius:10,padding:12}}>
              <h3 style={{fontSize:12,fontWeight:500,marginBottom:8,display:"flex",alignItems:"center",gap:6,color:"#fff"}}>
                <Heart size={13} color="#AFA9EC"/> Salute Organizzativa
              </h3>
              {enrichedProjects.slice(0,6).map(p=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",borderBottom:"1px solid rgba(255,255,255,.08)"}}>
                  <span style={{flex:1,fontSize:11,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</span>
                  <span style={{fontSize:15,fontWeight:700,color:coloreSuViola(p.health.color)}}>{p.health.score??"–"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Carico utenti */}
          {userLoad.length>0 && (
            <div style={{background:"#3C3489",borderRadius:10,padding:12,marginTop:8}}>
              <h3 style={{fontSize:12,fontWeight:500,marginBottom:8,display:"flex",alignItems:"center",gap:6,color:"#fff"}}>
                <Users size={13} color="#AFA9EC"/> Carico di lavoro per utente
              </h3>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:8}}>
                {userLoad.map(u=>{
                  const overloaded=u.open>10;
                  return (
                    <div key={u.name} style={{background:"rgba(255,255,255,.06)",borderRadius:8,padding:"8px 10px",
                      border: overloaded ? "1px solid #E24B4A" : "1px solid transparent"}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                        <div style={{width:20,height:20,borderRadius:"50%",background:"#7F77DD",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,flexShrink:0}}>{u.name[0]?.toUpperCase()}</div>
                        <span style={{fontSize:11,fontWeight:500,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</span>
                        {overloaded&&<div style={{width:6,height:6,borderRadius:"50%",background:"#E24B4A",marginLeft:"auto",flexShrink:0}}/>}
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <span style={{fontSize:10,color:"#AFA9EC"}}><span style={{fontWeight:500,color:"#fff",fontSize:13}}>{u.open}</span> aperte</span>
                        {u.overdue>0&&<span style={{fontSize:10,color:"#E24B4A",fontWeight:600}}>{u.overdue} scadute</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>)}

        {/* ─── PROGETTI ─── */}
        {activeTab==="projects" && (<>
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <div style={{position:"relative",flex:1,minWidth:200}}>
              <Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"#9ca3af"}}/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca progetto..."
                style={{width:"100%",paddingLeft:30,paddingRight:10,paddingTop:7,paddingBottom:7,border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
            </div>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
              style={{border:"1px solid #e5e7eb",borderRadius:7,padding:"7px 10px",fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"}}>
              <option value="tutti">Tutti gli stati</option>
              <option value="attivo">Attivo</option>
              <option value="completato">Completato</option>
              <option value="ritardo">In ritardo</option>
              <option value="non_avviato">Non avviato</option>
            </select>
            <select value={filterUser} onChange={e=>setFilterUser(e.target.value)}
              style={{border:"1px solid #e5e7eb",borderRadius:7,padding:"7px 10px",fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"}}>
              <option value="tutti">Tutti i responsabili</option>
              {allResponsibili.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            <select value={filterCliente} onChange={e=>setFilterCliente(e.target.value)}
              style={{border:"1px solid #e5e7eb",borderRadius:7,padding:"7px 10px",fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"}}>
              <option value="tutti">Tutti i clienti</option>
              {allClienti.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            {(search||filterStatus!=="tutti"||filterUser!=="tutti"||filterCliente!=="tutti")&&(
              <button onClick={()=>{setSearch("");setFilterStatus("tutti");setFilterUser("tutti");setFilterCliente("tutti");setFiltroScadenza("tutte");}} style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif"}}>
                <X size={13}/> Resetta
              </button>
            )}
            <span style={{fontSize:12,color:"#9ca3af",marginLeft:"auto"}}>{filteredProjects.length} progetti</span>
          </div>
          {/* Filtro scadenza commesse */}
          <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:11,color:"#9ca3af",fontWeight:600}}>Scadenza:</span>
            {[
              {val:"tutte",  label:"Tutte"},
              {val:"scadute",label:"🔴 Scadute",    color:"#dc2626"},
              {val:"7gg",    label:"🟠 Entro 7 gg", color:"#d97706"},
              {val:"mese",   label:"🟡 Questo mese",color:"#ca8a04"},
              {val:"senza_data", label:"— Senza data", color:"#9ca3af"},
            ].map(f=>(
              <button key={f.val} onClick={()=>setFiltroScadenza(f.val)}
                style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${filtroScadenza===f.val?(f.color||"#7c3aed"):"#e5e7eb"}`,
                  background:filtroScadenza===f.val?`${f.color||"#7c3aed"}15`:"#fff",
                  color:filtroScadenza===f.val?(f.color||"#7c3aed"):"#6b7280",
                  fontSize:11,fontWeight:filtroScadenza===f.val?700:400,
                  cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                {f.label}
              </button>
            ))}
          </div>

          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,overflow:"hidden"}}>
            {/* Header tabella */}
            <div style={{display:"grid",gridTemplateColumns:"2fr 110px 140px 80px 110px 70px 70px 70px 100px",borderBottom:"2px solid #e5e7eb",background:"#f9fafb"}}>
              {[
                {label:"Progetto",key:"name"},
                {label:"Salute ⓘ",key:"health",tooltip:"Punteggio 0-100 basato su: completamento attività, ritardi, criticità aperte, aggiornamenti recenti.\n90-100 Eccellente · 70-89 Buono · 50-69 Attenzione · 0-49 Critico"},
                {label:"Responsabili",key:null},
                {label:"Progresso",key:"progress"},
                {label:"Stato",key:"status"},
                {label:"Aperte",key:null},
                {label:"Scadute",key:"overdue"},
                {label:"Ore tot.",key:null,tooltip:"Somma ore di tutti i dipendenti dalla programmazione settimanale"},
                {label:"Aggiornato",key:"updated"},
              ].map((col,i)=>(
                <div key={i} onClick={()=>col.key&&toggleSort(col.key)} title={col.tooltip||""}
                  style={{padding:"10px 12px",fontSize:11,fontWeight:600,color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.04em",cursor:col.key?"pointer":"default",display:"flex",alignItems:"center",gap:4,userSelect:"none",position:"relative"}}>
                  {col.label}{col.key&&<SortIcon field={col.key}/>}
                </div>
              ))}
            </div>

            {filteredProjects.length===0
              ? <div style={{padding:40,textAlign:"center",color:"#9ca3af",fontSize:13}}>Nessun progetto trovato</div>
              : filteredProjects.map(p=>(
                <div key={p.id} style={{display:"grid",gridTemplateColumns:"2fr 110px 140px 80px 110px 70px 70px 70px 100px",borderBottom:"1px solid #f3f4f6",alignItems:"center"}}
                  onMouseOver={e=>e.currentTarget.style.background="#fafafa"}
                  onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                  <div style={{padding:"12px",display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:10,height:10,borderRadius:3,background:p.color||"#7c3aed",flexShrink:0}}/>
                    <div>
                      <CommessaLink commessa={p} style={{fontSize:13,fontWeight:600,display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{p.statoCommessa==="chiusa"&&<span style={{marginRight:4}} title="Chiusa">🔒</span>}{p.name}</CommessaLink>
                      <div style={{fontSize:11,color:"#9ca3af",display:"flex",gap:8}}>
                        <span>{p.total} attività</span>
                        {p.dataFine && (()=>{
                          const oggi3=new Date(); oggi3.setHours(0,0,0,0);
                          const df=new Date(p.dataFine+"T00:00:00");
                          const diff=Math.round((df-oggi3)/(1000*60*60*24));
                          const col=df<oggi3?"#dc2626":diff<=7?"#d97706":diff<=30?"#ca8a04":"#9ca3af";
                          const label=df<oggi3?`Scaduta ${Math.abs(diff)}gg fa`:diff===0?"Scade oggi":diff===1?"Scade domani":`Fine: ${p.dataFine.split("-").reverse().join("/")}`;
                          return <span style={{color:col,fontWeight:df<oggi3||diff<=7?700:400}}>· {label}</span>;
                        })()}
                      </div>
                    </div>
                  </div>
                  <div style={{padding:"12px",display:"flex",alignItems:"center",gap:6}}>
                    <HealthBadge score={p.health.score} color={p.health.color}/>
                    <span style={{fontSize:11,fontWeight:600,color:p.health.color}}>{p.health.label}</span>
                  </div>
                  <div style={{padding:"12px"}}>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {p.responsibili.slice(0,3).map(r=>(
                        <div key={r} style={{width:24,height:24,borderRadius:"50%",background:"#7c3aed",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700}} title={r}>{r[0]?.toUpperCase()}</div>
                      ))}
                      {p.responsibili.length>3&&<div style={{fontSize:11,color:"#9ca3af",alignSelf:"center"}}>+{p.responsibili.length-3}</div>}
                      {p.responsibili.length===0&&<span style={{fontSize:11,color:"#d1d5db"}}>—</span>}
                    </div>
                  </div>
                  <div style={{padding:"12px"}}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:3}}>{p.progress}%</div>
                    <div style={{height:5,background:"#f3f4f6",borderRadius:10,overflow:"hidden",width:50}}>
                      <div style={{width:`${p.progress}%`,height:"100%",background:p.progress===100?"#059669":p.overdue>0?"#dc2626":"#7c3aed",borderRadius:10}}/>
                    </div>
                  </div>
                  <div style={{padding:"12px"}}><StatusBadge status={p.status}/></div>
                  <div style={{padding:"12px",fontSize:13,fontWeight:600,color:p.open>0?"#2563eb":"#9ca3af"}}>{p.open||"—"}</div>
                  <div style={{padding:"12px",fontSize:13,fontWeight:600,color:p.overdue>0?"#dc2626":"#9ca3af",cursor:p.overdue>0?"pointer":"default"}}
                    onClick={()=>p.overdue>0&&openDrill(`Attività scadute — ${p.name}`, (projectTasks[p.id]||[]).filter(t=>isTaskOverdue(t)))}>
                    {p.overdue||"—"}
                  </div>
                  <div style={{padding:"12px",fontSize:13,fontWeight:700,color:(orePerCommessa[p.id]||0)>0?"#7c3aed":"#9ca3af"}} title="Ore totali dalla programmazione settimanale">
                    {(orePerCommessa[p.id]||0)>0?`${orePerCommessa[p.id]}h`:"—"}
                  </div>
                  <div style={{padding:"12px",fontSize:11,color:"#9ca3af"}}>{p.lastUpdated?format(new Date(p.lastUpdated),"d MMM",{locale:it}):"—"}</div>
                </div>
              ))
            }
          </div>

          {/* Drill-down in tab progetti */}
          {drillDown&&<div style={{marginTop:16}}><DrillDownList title={drillDown.title} tasks={drillDown.tasks} projects={projects} allTasks={tasks} onClose={()=>setDrillDown(null)}/></div>}
        </>)}

        {/* ─── GRAFICI ─── */}
        {activeTab==="charts" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:20}}>
              <h3 style={{fontSize:14,fontWeight:700,marginBottom:16}}>Distribuzione stati progetti</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart><Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value})=>`${name} (${value})`} labelLine={false} fontSize={11}>
                  {pieData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                </Pie><Tooltip/></PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:20}}>
              <h3 style={{fontSize:14,fontWeight:700,marginBottom:16}}>Carico per utente</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={userLoad} margin={{top:0,right:0,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                  <XAxis dataKey="name" tick={{fontSize:10}} tickLine={false}/>
                  <YAxis tick={{fontSize:11}} tickLine={false} axisLine={false}/>
                  <Tooltip/>
                  <Bar dataKey="open" fill="#7c3aed" name="Aperte" radius={[4,4,0,0]}/>
                  <Bar dataKey="overdue" fill="#dc2626" name="Scadute" radius={[4,4,0,0]}/>
                  <Legend wrapperStyle={{fontSize:12}}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:20}}>
              <h3 style={{fontSize:14,fontWeight:700,marginBottom:16}}>Avanzamento progetti</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={enrichedProjects.slice(0,8).map(p=>({name:p.name?.substring(0,14),progress:p.progress}))} layout="vertical" margin={{top:0,right:20,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false}/>
                  <XAxis type="number" domain={[0,100]} tick={{fontSize:10}} tickLine={false}/>
                  <YAxis dataKey="name" type="category" tick={{fontSize:10}} tickLine={false} width={90}/>
                  <Tooltip formatter={v=>`${v}%`}/>
                  <Bar dataKey="progress" fill="#7c3aed" name="Progresso %" radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:20}}>
              <h3 style={{fontSize:14,fontWeight:700,marginBottom:16}}>Salute progetti</h3>
              <div style={{overflowY:"auto",maxHeight:220}}>
                {[...enrichedProjects].sort((a,b)=>(a.health.score??-1)-(b.health.score??-1)).map(p=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",borderBottom:"1px solid #f9fafb"}}>
                    <div style={{width:8,height:8,borderRadius:2,background:p.color||"#7c3aed",flexShrink:0}}/>
                    <span style={{flex:1,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</span>
                    <div style={{width:80,height:6,background:"#f3f4f6",borderRadius:10,overflow:"hidden"}}>
                      <div style={{width:`${p.health.score??0}%`,height:"100%",background:p.health.color,borderRadius:10}}/>
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:p.health.color,width:28,textAlign:"right"}}>{p.health.score??"–"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── ALERT ─── */}
        {activeTab==="feed" && (
          <div>
            {/* Filtri */}
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
              <input value={filtroTesto} onChange={e=>setFiltroTesto(e.target.value)}
                placeholder="🔍 Cerca nel feed..."
                style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",width:220}}/>
              {["tutti","commesse","attivita","clienti","noteSpese","acquisti","rapportini","presenze","verbali","dipendenti"].map(cat=>(
                <button key={cat} onClick={()=>setFiltroCategoria(cat)}
                  style={{padding:"4px 10px",border:`1px solid ${filtroCategoria===cat?"#7c3aed":"#e5e7eb"}`,borderRadius:8,background:filtroCategoria===cat?"#ede9fe":"#fff",color:filtroCategoria===cat?"#7c3aed":"#374151",fontSize:11,fontWeight:filtroCategoria===cat?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",textTransform:"capitalize"}}>
                  {cat==="tutti"?"Tutti":cat}
                </button>
              ))}
            </div>

            {/* Timeline eventi */}
            <div style={{position:"relative"}}>
              <div style={{position:"absolute",left:20,top:0,bottom:0,width:2,background:"#e5e7eb",zIndex:0}}/>
              {eventi
                .filter(e=>filtroCategoria==="tutti"||e.categoria===filtroCategoria)
                .filter(e=>!filtroTesto||e.titolo?.toLowerCase().includes(filtroTesto.toLowerCase())||e.nomeUtente?.toLowerCase().includes(filtroTesto.toLowerCase()))
                .map((ev,i)=>{
                  const EMOJI = {commesse:"📁",attivita:"✔️",clienti:"🏢",noteSpese:"💳",acquisti:"🛒",rapportini:"📋",presenze:"🕐",verbali:"📝",dipendenti:"👷",economia:"💰"};
                  const PRIOCOL = {info:"#6b7280",medio:"#d97706",alto:"#ea580c",critico:"#dc2626"};
                  const col = PRIOCOL[ev.priorita]||"#6b7280";
                  const nav = ev.navigateTo ? (() => { try { return JSON.parse(ev.navigateTo); } catch { return null; } })() : null;
                  const ts = ev.createdAt?.toDate?.();
                  return (
                    <div key={ev.id} style={{display:"flex",gap:14,marginBottom:16,position:"relative",zIndex:1}}>
                      {/* Dot */}
                      <div style={{width:40,height:40,borderRadius:"50%",background:"#fff",border:`2px solid ${col}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,boxShadow:"0 1px 4px rgba(0,0,0,.08)"}}>
                        {EMOJI[ev.categoria]||"📌"}
                      </div>
                      {/* Card */}
                      <div style={{flex:1,background:"#fff",border:`1px solid #e5e7eb`,borderRadius:10,padding:"10px 14px",boxShadow:"0 1px 3px rgba(0,0,0,.05)"}}>
                        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:2}}>{ev.titolo}</div>
                            {ev.descrizione&&<div style={{fontSize:11,color:"#6b7280"}}>{ev.descrizione}</div>}
                            <div style={{fontSize:10,color:"#9ca3af",marginTop:4,display:"flex",gap:8}}>
                              <span>👤 {ev.nomeUtente||"Sistema"}</span>
                              {ts&&<span>🕐 {ts.toLocaleDateString("it-IT")} {ts.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})}</span>}
                              {ev.projectName&&<span>📁 <CommessaLink commessa={{id:ev.projectId,name:ev.projectName}}>{ev.projectName}</CommessaLink></span>}
                              {ev.clienteNome&&<span>🏢 <ClienteLink cliente={{id:ev.clienteId,ragioneSociale:ev.clienteNome}}>{ev.clienteNome}</ClienteLink></span>}
                            </div>
                          </div>
                          {nav&&onNavigate&&(
                            <button onClick={()=>onNavigate(nav)}
                              style={{fontSize:11,padding:"3px 8px",border:"1px solid #e5e7eb",borderRadius:6,background:"#f9fafb",color:"#374151",cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0,whiteSpace:"nowrap"}}>
                              Vai →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              }
{eventi.filter(e=>filtroCategoria==="tutti"||e.categoria===filtroCategoria).length===0&&(
                <EmptyState tipo="feed" titolo="Feed vuoto" descrizione="Gli eventi appariranno qui man mano che vengono registrate operazioni in Nexiora."/>
              )}
            </div>
          </div>
        )}

        {activeTab==="audit" && (
          <div>
            {/* Filtri */}
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
              <input value={auditFiltroTesto} onChange={e=>setAuditFiltroTesto(e.target.value)}
                placeholder="🔍 Cerca nel log..."
                style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",width:220}}/>
              {["tutti","commesse","attivita","economia","dipendenti","noteSpese","clienti","segnalazioni","contratti-manutenzione","piattaforma"].map(s=>(
                <button key={s} onClick={()=>setAuditFiltroSezione(s)}
                  style={{padding:"4px 10px",border:`1px solid ${auditFiltroSezione===s?"#7c3aed":"#e5e7eb"}`,borderRadius:8,background:auditFiltroSezione===s?"#ede9fe":"#fff",color:auditFiltroSezione===s?"#7c3aed":"#374151",fontSize:11,fontWeight:auditFiltroSezione===s?700:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",textTransform:"capitalize"}}>
                  {s==="tutti"?"Tutti":s}
                </button>
              ))}
              <span style={{marginLeft:"auto",fontSize:11,color:"#9ca3af"}}>{auditLog.length} modifiche registrate</span>
            </div>

            {/* Tabella log */}
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,overflow:"hidden"}}>
              <table style={{borderCollapse:"collapse",width:"100%",fontSize:12}}>
                <thead>
                  <tr style={{background:"#1e1b4b"}}>
                    <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:130}}>Quando</th>
                    <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:120}}>Chi</th>
                    <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:100}}>Sezione</th>
                    <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Oggetto</th>
                    <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Campo</th>
                    <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Prima</th>
                    <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Dopo</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog
                    .filter(r=>auditFiltroSezione==="tutti"||r.sezione===auditFiltroSezione)
                    .filter(r=>!auditFiltroTesto||
                      r.nomeUtente?.toLowerCase().includes(auditFiltroTesto.toLowerCase())||
                      r.refNome?.toLowerCase().includes(auditFiltroTesto.toLowerCase())||
                      r.campoLabel?.toLowerCase().includes(auditFiltroTesto.toLowerCase())||
                      r.before?.toLowerCase().includes(auditFiltroTesto.toLowerCase())||
                      r.after?.toLowerCase().includes(auditFiltroTesto.toLowerCase())
                    )
                    .map((r,i)=>{
                      const ts = r.createdAt?.toDate?.();
                      const SEZIONE_COL = {commesse:"#7c3aed",attivita:"#2563eb",economia:"#059669",dipendenti:"#d97706",noteSpese:"#0891b2",clienti:"#db2777",segnalazioni:"#9333ea","contratti-manutenzione":"#0d9488",piattaforma:"#dc2626"};
                      const col = SEZIONE_COL[r.sezione]||"#6b7280";
                      return (
                        <tr key={r.id} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                          <td style={{padding:"7px 12px",color:"#9ca3af",fontSize:11,whiteSpace:"nowrap"}}>
                            {ts?ts.toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit"})+" "+ts.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"}):"—"}
                          </td>
                          <td style={{padding:"7px 12px",fontWeight:600,color:"#374151",fontSize:11}}>{r.nomeUtente||"—"}</td>
                          <td style={{padding:"7px 12px"}}>
                            <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:6,background:col+"18",color:col}}>
                              {r.sezione}
                            </span>
                          </td>
                          <td style={{padding:"7px 12px",color:"#374151",fontSize:11,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.refNome||"—"}</td>
                          <td style={{padding:"7px 12px",color:"#6b7280",fontSize:11}}>{r.campoLabel||r.campo||"—"}</td>
                          <td style={{padding:"7px 12px"}}>
                            <span style={{fontSize:11,padding:"1px 6px",borderRadius:4,background:"#fee2e2",color:"#dc2626",fontFamily:"monospace"}}>{r.before||"—"}</span>
                          </td>
                          <td style={{padding:"7px 12px"}}>
                            <span style={{fontSize:11,padding:"1px 6px",borderRadius:4,background:"#d1fae5",color:"#059669",fontFamily:"monospace"}}>{r.after||"—"}</span>
                          </td>
                        </tr>
                      );
                    })
                  }
                  {auditLog.filter(r=>auditFiltroSezione==="tutti"||r.sezione===auditFiltroSezione).length===0&&(
                    <tr><td colSpan={7} style={{padding:40,textAlign:"center",color:"#9ca3af"}}>
                      <div style={{fontSize:14,marginBottom:6}}>📋</div>
                      Nessuna modifica registrata. Le modifiche appariranno qui man mano che vengono effettuate.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab==="alerts" && (
          <div>
            {alerts.length===0
              ? <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
                  <CheckCircle size={40} color="#d1d5db" style={{margin:"0 auto 12px",display:"block"}}/>
                  <h3 style={{fontSize:16,fontWeight:600,color:"#6b7280",marginBottom:8}}>Nessun alert attivo</h3>
                  <p style={{fontSize:13}}>L'organizzazione è in ottima salute!</p>
                </div>
              : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
                {alerts.map((a,i)=>{
                  const critico = a.type==="error";
                  const dotColor = critico ? "#E24B4A" : a.type==="warning" ? "#EF9F27" : "#7F77DD";
                  return (
                    <div key={i} onClick={a.onClick} style={{background:"#3C3489",borderRadius:12,padding:"14px 16px",position:"relative",
                      border: critico ? "2px solid #E24B4A" : "1px solid transparent",
                      cursor:a.onClick?"pointer":"default"}}
                      onMouseOver={e=>{if(a.onClick)e.currentTarget.style.opacity="0.85";}}
                      onMouseOut={e=>{e.currentTarget.style.opacity="1";}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:dotColor,position:"absolute",top:14,right:14}}/>
                      <div style={{display:"flex",alignItems:"flex-start",gap:10,paddingRight:20}}>
                        <a.icon size={16} color="#AFA9EC" style={{flexShrink:0,marginTop:1}}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:500,color:"#fff"}}>{a.text}</div>
                          <div style={{fontSize:11,color:"#AFA9EC",marginTop:4}}>{critico?"Critico":"Attenzione"} · rilevato ora</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            }
          </div>
        )}
      </div>
    </div>
  );
}
